import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Placement } from '@floating-ui/react';
import classNames from 'classnames';
import { PopoverMenu } from '../index';

export type FilterableDropdownOption = { label: string; value: string };

export interface FilterableDropdownProps {
  name: string;
  options: FilterableDropdownOption[];
  value?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  onChange?: (event: { target: { name: string; value: string | null } }) => void;
  onBlur?: (event: { target: { name: string; value: string | null } }) => void;
  onClear?: () => void;
  className?: string;
  invalid?: boolean;
  offset?: number;
  placement?: Placement;
  popoverMenuPortal?: boolean;
  contentWrapperClassName?: string;
  maxVisibleOptions?: number;
}

const stripHtml = (value: string) => value?.replace(/<(p)>/g, '').replace(/<\/(p)>/g, '') || '';

type NormalizedFilterableDropdownOption = FilterableDropdownOption & {
  displayLabel: string;
  searchLabel: string;
};

const FilterableDropdown = ({
  name,
  options,
  value,
  placeholder,
  searchPlaceholder = 'Search',
  onChange,
  onBlur,
  onClear,
  className,
  invalid,
  offset,
  placement,
  popoverMenuPortal,
  contentWrapperClassName,
  maxVisibleOptions = 80,
}: FilterableDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [_value, set_value] = useState<string | null>(value ?? null);
  const [title, setTitle] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(maxVisibleOptions);
  const deferredQuery = useDeferredValue(query);
  const wrapper = useRef<HTMLUListElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);

  const normalizedOptions = useMemo<NormalizedFilterableDropdownOption[]>(
    () =>
      options.map((option) => {
        const displayLabel = stripHtml(option.label);

        return {
          ...option,
          displayLabel,
          searchLabel: displayLabel.toLowerCase(),
        };
      }),
    [options],
  );

  const filteredOptions = useMemo(() => {
    const normalizedQuery = deferredQuery.trim().toLowerCase();

    if (!normalizedQuery) {
      return normalizedOptions;
    }

    return normalizedOptions.filter((option) => option.searchLabel.includes(normalizedQuery));
  }, [normalizedOptions, deferredQuery]);

  const visibleOptions = useMemo(
    () => filteredOptions.slice(0, visibleCount),
    [filteredOptions, visibleCount],
  );

  const _onChange = (option: NormalizedFilterableDropdownOption) => {
    set_value(option.value);
    const event = { target: { name, value: option.value } };
    onChange?.(event);

    setOpen(false);
    setQuery('');
    if (onBlur) {
      setTimeout(() => {
        onBlur(event);
      }, 0);
    }
  };

  useEffect(() => {
    if (_value != null) {
      const opt = normalizedOptions?.find((o) => o.value === _value);
      setTitle(opt?.displayLabel ?? null);
    } else {
      setTitle(null);
    }
  }, [_value, normalizedOptions]);

  useEffect(() => {
    if (value !== null && value !== undefined) {
      set_value(value);
    } else {
      set_value(null);
      setTitle(null);
    }
  }, [value]);

  useEffect(() => {
    setVisibleCount(maxVisibleOptions);
    wrapper.current?.scrollTo({ top: 0 });
  }, [deferredQuery, maxVisibleOptions, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const focusTimer = window.setTimeout(() => searchInput.current?.focus(), 0);

    const keydown = (event: KeyboardEvent) => {
      const focusOption = wrapper.current?.querySelector('.option:focus');
      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          if (focusOption) {
            const prev = focusOption.previousElementSibling as HTMLElement | null | undefined;
            prev?.focus();
          } else {
            const last = wrapper.current?.querySelector('.option:last-child') as HTMLElement | null | undefined;
            last?.focus();
          }
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (document.activeElement === searchInput.current) {
            const first = wrapper.current?.querySelector('.option') as HTMLElement | null | undefined;
            first?.focus();
          } else {
            const next = focusOption?.nextElementSibling as HTMLElement | null | undefined;
            next?.focus();
          }
          break;
        case 'Enter':
          if (focusOption) {
            event.preventDefault();
            (focusOption as HTMLElement | null | undefined)?.click();
          }
          break;
      }
    };

    window.addEventListener('keydown', keydown);

    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', keydown);
    };
  }, [open]);

  const handleClear = () => {
    set_value(null);
    setTitle(null);
    setQuery('');
    onClear?.();
    const event = { target: { name, value: null } };
    onChange?.(event);
  };

  const handleOptionsScroll = () => {
    const list = wrapper.current;

    if (!list || visibleCount >= filteredOptions.length) {
      return;
    }

    const distanceFromBottom = list.scrollHeight - list.scrollTop - list.clientHeight;

    if (distanceFromBottom < 80) {
      setVisibleCount((current) => Math.min(current + maxVisibleOptions, filteredOptions.length));
    }
  };

  return (
    <PopoverMenu
      open={open}
      title={title || placeholder || ''}
      titleClass={!title && placeholder ? 'placeholder' : ''}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setQuery('');
      }}
      className={classNames(
        title && 'selected',
        '[&_.main-button]:h-11 [&_.main-button]:rounded-[6px] [&_.main-button]:border [&_.main-button]:bg-white [&_.main-button]:px-3 [&_.main-button]:text-sm [&_.main-button]:text-zinc-800',
        '[&_.popover-title]:truncate',
        invalid ? '[&_.main-button]:border-red-500' : '[&_.main-button]:border-zinc-200',
        className,
      )}
      offset={offset || 4}
      placement={placement}
      portal={popoverMenuPortal}
      contentWrapperClassName={contentWrapperClassName}
      showClearIcon={onClear && !!title}
      onClearIconClick={handleClear}
    >
      <div className="w-[min(360px,calc(100vw-32px))]">
        <div className="border-b border-zinc-100 p-2">
          <input
            ref={searchInput}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchPlaceholder}
            className="h-10 w-full rounded-[6px] border border-zinc-200 px-3 text-sm text-zinc-800 outline-none placeholder:text-zinc-400 focus:border-zinc-400"
          />
        </div>
        <ul
          className="max-h-72 w-full overflow-y-auto py-2 text-sm text-gray-700 dark:text-gray-200"
          ref={wrapper}
          onScroll={handleOptionsScroll}
        >
          {filteredOptions.length > 0 ? (
            visibleOptions.map((option) => (
              <li
                key={option.value}
                onClick={() => _onChange(option)}
                className={classNames(
                  option.value === _value && 'selected bg-gray-300 hover:!bg-gray-300',
                  'option outline-none hover:bg-gray-100 focus:bg-gray-100',
                )}
                tabIndex={open ? 0 : -1}
              >
                <div className="block cursor-pointer px-4 py-2">{option.displayLabel}</div>
              </li>
            ))
          ) : (
            <li className="px-4 py-3 text-sm text-zinc-500">No results found</li>
          )}
          {visibleCount < filteredOptions.length && (
            <li className="px-4 py-2 text-xs text-zinc-500">
              Showing {visibleOptions.length} of {filteredOptions.length}. Keep scrolling for more.
            </li>
          )}
        </ul>
      </div>
    </PopoverMenu>
  );
};

export default FilterableDropdown;
